import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMinimizeAnimationOptions {
  isMinimized: boolean;
  sourceSelector: string;
  onMinimize: () => void;
  onBeforeMinimize?: () => void;
  minimizeDelayMs?: number;
  resetDelayMs?: number;
}

interface UseMinimizeAnimationReturn {
  isMinimizing: boolean;
  targetRef: RefObject<HTMLDivElement>;
  requestMinimize: () => void;
}

const DEFAULT_MINIMIZE_DELAY_MS = 250;
const DEFAULT_RESET_DELAY_MS = 300;

/**
 * Hook utilitaire pour animer un panneau vers une cible visuelle
 * avant de basculer son etat en "minimise".
 *
 * Utilisé dans le formulaire d'ajout de dossier pour animer la transition vers le bouton de brouillon minimise.
 */
export default function useMinimizeAnimation({
  isMinimized,
  sourceSelector,
  onMinimize,
  onBeforeMinimize,
  minimizeDelayMs = DEFAULT_MINIMIZE_DELAY_MS,
  resetDelayMs = DEFAULT_RESET_DELAY_MS,
}: UseMinimizeAnimationOptions): UseMinimizeAnimationReturn {
  const [isMinimizing, setIsMinimizing] = useState(false);
  // Element destination de l'animation (ex: bouton "draft minimise").
  const targetRef = useRef<HTMLDivElement>(null);
  const minimizeTimeoutRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (minimizeTimeoutRef.current) {
      window.clearTimeout(minimizeTimeoutRef.current);
      minimizeTimeoutRef.current = null;
    }

    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  const applyAnimationOffsets = useCallback(() => {
    const sourceEl = document.querySelector(
      sourceSelector,
    ) as HTMLElement | null;
    const targetEl = targetRef.current;
    if (!sourceEl || !targetEl) {
      return;
    }

    const sourceRect = sourceEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const sourceCenterX = sourceRect.left + sourceRect.width / 2;
    const sourceCenterY = sourceRect.top + sourceRect.height / 2;

    // On expose le delta via variables CSS pour laisser le style piloter
    // la transformation (translate/scale) sans logique JS supplementaire.
    sourceEl.style.setProperty(
      '--minimize-x',
      `${targetRect.left - sourceCenterX}px`,
    );
    sourceEl.style.setProperty(
      '--minimize-y',
      `${targetRect.top - sourceCenterY}px`,
    );
  }, [sourceSelector]);

  const requestMinimize = useCallback(() => {
    // Permet de persister un etat/form avant de lancer l'animation.
    onBeforeMinimize?.();
    if (isMinimizing) {
      // Evite de lancer deux minimisations concurrentes.
      return;
    }

    applyAnimationOffsets();
    setIsMinimizing(true);

    if (minimizeTimeoutRef.current) {
      window.clearTimeout(minimizeTimeoutRef.current);
    }

    // Delai court pour laisser l'animation demarrer avant le changement d'etat.
    minimizeTimeoutRef.current = window.setTimeout(() => {
      onMinimize();
    }, minimizeDelayMs);
  }, [
    applyAnimationOffsets,
    isMinimizing,
    minimizeDelayMs,
    onBeforeMinimize,
    onMinimize,
  ]);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!isMinimizing || !isMinimized) {
      return;
    }

    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
    }

    // On retire le flag "isMinimizing" apres la fin visuelle de l'animation.
    resetTimeoutRef.current = window.setTimeout(() => {
      setIsMinimizing(false);
    }, resetDelayMs);
  }, [isMinimized, isMinimizing, resetDelayMs]);

  return {
    isMinimizing,
    targetRef,
    requestMinimize,
  };
}
